// src/Pages/Day/DayForm.js
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { HiOutlineArrowLeft } from "react-icons/hi";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { Formik, Form, Field } from "formik";
import { Geolocation } from "@capacitor/geolocation";
import { Spinner } from "../../Components/Common";
import { HOME } from "../../routes";
import { MESSAGES } from "./constants";

const libraries = ["places"];

const DayForm = ({ mode = "start", onSubmit }) => {
  const containerStyle = { width: "100%", height: "300px" };
  const navigate = useNavigate();
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [submitting, setSubmitting] = useState(false);
  const [isGeoReady, setIsGeoReady] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const getGeoLocation = async () => {
    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });
      setLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });
      setIsGeoReady(true);
    } catch (error) {
      toast.error(MESSAGES.LOCATION_DENIED_ERROR);
    }
  };

  useEffect(() => {
    getGeoLocation();
  }, []);

  const initialValues = {
    date: new Date().toISOString().split("T")[0],
    time: new Date().toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };

  const handleSubmit = async (values) => {
    if (submitting) return;
    setSubmitting(true);

    const formData = new FormData();
    if (mode === "start") {
      formData.append("date", values.date);
    }
    formData.append(`${mode}_time`, values.time);
    formData.append(`${mode}_latitude`, location.latitude);
    formData.append(`${mode}_longitude`, location.longitude);

    try {
      const res = await onSubmit(formData);
      if (res.status === 201 || res.status === 200) {
        toast.success(
          mode === "start" ? MESSAGES.STARTED_DAY : MESSAGES.ENDED_DAY
        );
        navigate(HOME);
      } else {
        toast.error(MESSAGES.FAILED_SUBMISSION);
      }
    } catch (e) {
      toast.error(MESSAGES.FAILED_SUBMISSION);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) return <div>{MESSAGES.MAP_LOADING_ERROR}</div>;
  if (!isLoaded || !isGeoReady) {
    return (
      <div className="flex justify-center mt-4">
        <Spinner size={30} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto rounded-lg flex justify-center items-center">
      <div className="bg-white rounded-lg p-2 w-full max-w-3xl shadow-md">
        <div className="flex items-center justify-start mb-4 pl-2 gap-20 md:gap-64">
          <button
            type="button"
            onClick={() => navigate(HOME)}
            className="text-primary hover:text-secondary text-2xl"
            title="Back"
          >
            <HiOutlineArrowLeft />
          </button>
          <h2 className="text-2xl font-bold text-primary text-center">
            {mode === "start" ? "Start the Day" : "End the Day"}
          </h2>
        </div>

        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {() => (
            <Form className="text-secondary px-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-medium">Date</label>
                  <Field
                    name="date"
                    type="date"
                    disabled
                    className="w-full border border-gray-300 rounded px-3 mt-1 text-gray-500 bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block font-medium">
                    {mode === "start" ? "Start Time" : "End Time"}
                  </label>
                  <Field
                    name="time"
                    type="time"
                    disabled
                    className="w-full border border-gray-300 rounded px-3 mt-1 text-gray-500 bg-gray-100"
                  />
                </div>
              </div>

              <label className="block font-medium mt-4">Current Location</label>
              <div className="mt-1 rounded overflow-hidden border">
                <GoogleMap
                  onLoad={() => {}}
                  mapContainerStyle={containerStyle}
                  center={{
                    lat: parseFloat(location.latitude),
                    lng: parseFloat(location.longitude),
                  }}
                  zoom={14}
                  options={{
                    gestureHandling: "none",
                    draggable: false,
                    zoomControl: false,
                    keyboardShortcuts: false,
                    scrollwheel: false,
                  }}
                >
                  <Marker
                    position={{
                      lat: parseFloat(location.latitude),
                      lng: parseFloat(location.longitude),
                    }}
                  />
                </GoogleMap>
              </div>

              <div className="flex justify-between mt-8">
                <button
                  type="button"
                  onClick={() => navigate(HOME)}
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`px-4 py-2 text-white rounded ${
                    submitting
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-primary hover:bg-secondary"
                  }`}
                >
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default DayForm;
